import { useState } from 'react';

import { TASK_CONST } from '@/constant';
import {
  useDebouncedState,
  useFrameCountQuery,
  useFrameListQuery,
  useProjectDetailQuery,
  useTaskDetailQuery,
} from '@/hook';
import { Close, VFlex, VStack, VTab, VTabContent } from '@/package';
import type { Task, User } from '@/type';
import { uuid } from '@/util';

import { BoxDataOperation } from './BoxDataOperation';
import { PcdCacheStatus } from './PCDCacheStatus';
import PreAnnotationOperation from './PreAnnotationOperation';
import { TaskContributionSummary } from './TaskContributionSummary';
import { TaskDetailHeader } from './TaskDetailHeader';
import { TaskDetailTabOperation } from './TaskDetailTabOperation';
import { TaskFrameList } from './TaskFrameList';
import { TaskMemberProgressList } from './TaskMemberProgressList';
import { TaskOverview } from './TaskOverview';
import { TaskStatistics } from './TaskStatistics';

const ALL_MEMBER_VALUE = uuid();

const TaskDetail = ({
  task,
  deselectTask,
}: {
  task: Task;
  deselectTask: () => void;
}) => {
  const { data: taskDetail } = useTaskDetailQuery(task.id);
  const { data: projectData } = useProjectDetailQuery(
    Number(taskDetail?.projectID),
  );
  const { data: frameCount } = useFrameCountQuery({ taskID: task.id });
  const {
    state: keyword,
    debouncedState: debouncedKeyword,
    onChange: onKeywordChange,
  } = useDebouncedState('');
  const [selectedMember, setSelectedMember] =
    useState<User['id']>(ALL_MEMBER_VALUE);
  const { data: frameData } = useFrameListQuery({
    taskID: task.id,
    frameSeq: debouncedKeyword ? Number(debouncedKeyword) : null,
  });
  const frameList = frameData?.frames ?? [];
  const safeSelectedMember =
    selectedMember === ALL_MEMBER_VALUE ? undefined : selectedMember;

  return (
    <VStack className="h-full w-full overflow-y-hidden bg-white">
      <VStack className="w-full gap-4 px-8 py-[21px]">
        <VFlex items="center" justify="between">
          <div className="text-headline-sm text-gray-600">{taskDetail?.id}</div>

          <VFlex gap={0.25} items="center">
            <PcdCacheStatus task={task} />
            <div className="h-5 w-px bg-gray-300" />
            <Close
              role="button"
              onClick={deselectTask}
              className="size-6 text-gray-400"
            />
          </VFlex>
        </VFlex>

        <VStack gap={1}>
          {taskDetail && <TaskDetailHeader task={taskDetail} />}
          <VStack gap={0.5} className="w-full">
            <VFlex gap={1} className="w-full">
              {taskDetail && <PreAnnotationOperation task={taskDetail} />}
              {taskDetail && <BoxDataOperation task={taskDetail} />}
            </VFlex>
          </VStack>
        </VStack>
      </VStack>

      <VTab defaultValue="0" className="flex-1 overflow-hidden">
        <TaskDetailTabOperation
          task={task}
          keyword={keyword}
          onKeywordChange={onKeywordChange}
          selectedMember={selectedMember}
          setSelectedMember={setSelectedMember}
          allMemberValue={ALL_MEMBER_VALUE}
        />

        <VStack className="w-full flex-1 overflow-y-hidden">
          <div className="w-full flex-1 overflow-y-auto px-8 py-2">
            <VTabContent value="0">
              <VStack className="w-full" gap={0.5}>
                {taskDetail &&
                  (taskDetail.state === TASK_CONST.TASK_WORKSTATE.COMPLETED ? (
                    <TaskContributionSummary taskDetail={taskDetail} />
                  ) : (
                    <TaskMemberProgressList taskDetail={taskDetail} />
                  ))}

                <TaskFrameList
                  frameCount={frameCount ?? 0}
                  frameList={frameList}
                  taskDetail={taskDetail ?? task}
                />
              </VStack>
            </VTabContent>

            <VTabContent value="1">
              {taskDetail && (
                <TaskStatistics
                  taskDetail={taskDetail}
                  selectedMember={safeSelectedMember}
                />
              )}
            </VTabContent>

            <VTabContent value="2">
              {taskDetail && projectData && (
                <TaskOverview
                  projectData={projectData}
                  taskDetail={taskDetail}
                />
              )}
            </VTabContent>
          </div>
        </VStack>
      </VTab>
    </VStack>
  );
};

export default TaskDetail;
